// Prevents the same keyed async action from running twice while an earlier
// call for that key is still in flight - e.g. a confirm action (publish/
// delete/archive/syndicate) on a Copilot result card, where a double-click
// must not dispatch the destructive request twice. A caller-side `disabled`
// state alone doesn't close this: it only takes effect once React commits
// the re-render, and two synchronous calls in the same tick can both read
// the old (not-yet-disabled) state before either commits.
export function createPendingActionGuard() {
  const pending = new Set();

  // Runs `fn()` for `key` unless a call for that same key is already in
  // flight, in which case this is a no-op that resolves to `undefined`
  // without invoking `fn` - the caller can `await run(...)` either way.
  // The key is released once `fn()` settles, success or failure, so a
  // failed action can be retried under the same key.
  const run = async (key, fn) => {
    if (pending.has(key)) return undefined;
    pending.add(key);
    try {
      return await fn();
    } finally {
      pending.delete(key);
    }
  };

  const isPending = (key) => pending.has(key);

  return { run, isPending };
}
